import React, { useRef, useState } from 'react'
import styled from 'styled-components'
import { SelectInputStyled } from '@edulastic/common'
import { accessibilityColors } from '@edulastic/colors'
import { IconClose } from '@edulastic/icons'
import { Button, Input, Select } from 'antd'
import { segmentApi } from '@edulastic/api'
import { connect } from 'react-redux'
import {
  onEnterKeyPressHandler,
  setSelectSearchAriaLabel,
} from '../../../../common/utils/helpers'
import { getUserFeatures } from '../../../src/selectors/user'

const { Option } = Select

const InputTag = ({
  onSearchInputChange,
  value = [],
  placeholder = 'Search by skills and keywords',
  disabled = false,
  computeSuggestions,
  suggestions = [],
  library,
  userFeatures,
}) => {
  const inputTagRef = useRef()
  const parentContainerRef = useRef()
  const placeholderRef = useRef()
  const [dropDownOpen, setDropDownOpen] = useState(false)

  const [typeaheadData, setTypeaheadData] = useState({ x: 0, y: 0 })

  const computeSuggestionsCallback = userFeatures.allowAutoComplete
    ? computeSuggestions
    : undefined

  return (
    <Container
      hideSearchByStyle={!userFeatures.allowAutoComplete}
      ref={parentContainerRef}
    >
      <SelectInputStyled
        mode="tags"
        placeholder={placeholder}
        onChange={(newValues) => {
          onSearchInputChange(newValues)
          if (newValues.length > value?.length || 0) {
            computeSuggestionsCallback?.({ target: { value: '' } })
          }
          setTimeout(() => {
            const inputRef = inputTagRef?.current?.rcSelect?.inputRef
            if (inputRef) {
              const rect = inputRef.getBoundingClientRect()
              setTypeaheadData({
                x: rect.left,
                y: rect.top,
              })
            }
          }, 400)
        }}
        style={{ width: '100%', minHeight: '40px' }}
        value={value}
        getPopupContainer={(triggerNode) => triggerNode.parentNode}
        disabled={disabled}
        onSearch={(e) => {
          computeSuggestionsCallback?.({ target: { value: e } })
        }}
        onSelect={() => {
          segmentApi.genericEventTrack('autoCompleteSearch', {
            library,
          })
        }}
        onDropdownVisibleChange={setDropDownOpen}
        onInputKeyDown={(e) => {
          if (
            value.length >= 5 &&
            e.key !== 'Backspace' &&
            e.key !== 'Delete'
          ) {
            e.preventDefault()
          }

          if (e.key == 'Escape') {
            if (inputTagRef.current && inputTagRef.current.rcSelect) {
              const selectInstance = inputTagRef.current.rcSelect
              // Access the internal updater
              // WARNING THIS WILL BREAK ON REACT VERSION UPDATE
              selectInstance.updater.enqueueSetState(
                selectInstance,
                { inputValue: '' },
                null,
                'setState'
              )
            }
            computeSuggestionsCallback?.({ target: { value: '' } })
            document.activeElement?.blur?.()
          }

          const inputRef = inputTagRef?.current?.rcSelect?.inputRef
          if (inputRef) {
            const rect = inputRef.getBoundingClientRect()
            setTypeaheadData({
              x: rect.left,
              y: rect.top,
            })
          }
          if (e.key == 'Enter') {
            onSearchInputChange
            if (inputTagRef.current && inputTagRef.current.rcSelect) {
              const selectInstance = inputTagRef.current.rcSelect
              // Access the internal updater
              // WARNING THIS WILL BREAK ON REACT VERSION UPDATE
              let valueActive = ''
              if (parentContainerRef.current) {
                const activeItem = parentContainerRef.current.querySelector(
                  '.ant-select-dropdown-menu-item-active'
                )
                if (activeItem) {
                  valueActive = activeItem.innerText
                }
              }
              if (
                (selectInstance && valueActive) ||
                selectInstance.state.inputValue
              ) {
                onSearchInputChange([
                  ...value,
                  valueActive || selectInstance.state.inputValue,
                ])
                selectInstance.updater.enqueueSetState(
                  selectInstance,
                  { inputValue: '' },
                  null,
                  'setState'
                )
                computeSuggestionsCallback?.({ target: { value: '' } })
              }
            }
          }

          if (e.key == 'Tab' && suggestions && suggestions.length && inputRef) {
            setTimeout(() => {
              const suggestionToTypeAhead = suggestions[0]
              inputRef.value = suggestionToTypeAhead
              inputRef.style.width = 'min-content'
              if (inputTagRef.current && inputTagRef.current.rcSelect) {
                const selectInstance = inputTagRef.current.rcSelect

                // Access the internal updater
                // WARNING THIS WILL BREAK ON REACT VERSION UPDATE
                selectInstance.updater.enqueueSetState(
                  selectInstance,
                  { inputValue: suggestions[0] },
                  null,
                  'setState'
                )
              }
              segmentApi.genericEventTrack('autoCompleteTypeAhead', {
                library,
              })
            }, 10)
          }
        }}
        removeIcon={
          <StyledButton
            tabIndex={0}
            onKeyDown={(e) =>
              onEnterKeyPressHandler(e, (event) => event.target.click())
            }
            aria-label="Remove Search Tag"
          >
            <StyledIconClose aria-hidden="true" />
          </StyledButton>
        }
        ref={(ref) => {
          inputTagRef.current = ref
          setSelectSearchAriaLabel(ref, placeholder)
        }}
        defaultActiveFirstOption={false}
        showArrow={false}
        showSearch={userFeatures.allowAutoComplete}
        notFoundContent={null}
        filterOption={false}
        open={!!userFeatures.allowAutoComplete && suggestions?.length}
      >
        {!!userFeatures.allowAutoComplete &&
          suggestions.map((v) => <Option key={v}>{v}</Option>)}
      </SelectInputStyled>
      <TypeaheadPlaceholderContainer
        x={typeaheadData.x}
        y={typeaheadData.y}
        display={dropDownOpen && suggestions && !!suggestions.length}
        ref={placeholderRef}
        disabled
        value={suggestions[0]}
      />
    </Container>
  )
}

const enahnce = connect((state) => ({
  userFeatures: getUserFeatures(state),
}))

export default enahnce(InputTag)

const StyledButton = styled(Button)`
  width: fit-content;
  padding: 0px;
  height: 15px;
  background: none;
  outline: none;
  border: none;
  margin-top: 0px;
  margin-right: -3px;
  &:hover {
    background: none;
  }
  &:focus {
    outline: 2px solid ${accessibilityColors.theme.blue};
    background: none;
  }
`

const StyledIconClose = styled(IconClose)`
  width: 10px;
  height: 10px !important;
`

const Container = styled.div`
  background: white;
  display: flex;
  flex-wrap: wrap;
  min-height: 40px;
  width: 100%;
  .ant-select-dropdown-menu {
    display: ${({ hideSearchByStyle }) =>
      hideSearchByStyle ? 'none !important' : ''};
  }
  .ant-select-selection__choice {
    border-radius: 4px;
    height: 24px;
    display: flex;
    align-items: center;
    border: none;
    background: ${accessibilityColors.labels.grey.bg};
    color: ${accessibilityColors.labels.grey.text};
    font-weight: 600;
    margin: 5px 0 5px 5px;
    font-size: 12px;
  }
  .ant-select-selection__rendered {
    margin-top: 4px;
  }
`

const TypeaheadPlaceholderContainer = styled(Input)`
  position: absolute;
  display: ${({ display }) => (display ? 'block' : 'none')};
  left: ${({ x }) => x - 70.5}px;
  top: ${({ y }) => y - 4.5}px;
  padding: 0px;
  font-family: 'Open Sans';
  font-weight: 400;
  border: none !important;
  box-shadow: none !important;
  outline: none !important;
  background: none !important;
  cursor: text !important;
  transform: scaleX(0.93);
  transform-origin: 20px 0px;
  transition: none !important;
`
